<template>
  <div class="admin-management container">
    <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/admin">Admin Settings</router-link></li>
        <li class="is-active"><router-link to="/dashboard/permission-management" aria-current="true">Permission Management</router-link></li>
      </ul>
    </nav>

    <h1 class="title">Admin Management</h1>

    <!-- Tabs -->
    <div class="tabs">
      <ul>
        <li :class="{ 'is-active': activeTab === 'groups' }" @click="activeTab = 'groups'">
          <a>Group Permissions</a>
        </li>
        <li :class="{ 'is-active': activeTab === 'storecodes' }" @click="activeTab = 'storecodes'">
          <a>Department Code Management</a>
        </li>
      </ul>
    </div>

    <!-- Group Permission Management -->
    <div v-if="activeTab === 'groups'">
      <div class="buttons mb-3">
        <button class="button is-small is-primary" @click="showGroupModal(null)">Add New Group</button>
        <button class="button is-small is-link" @click="downloadAdminReportCsv">
          <span>Admin Report</span>
        </button>
      </div>


      <table class="table is-striped is-fullwidth is-size-7">
        <thead>
          <tr>
            <th>Group Name</th>
            <th>Permissions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in groups" :key="group.id">
            <td>{{ group.name }}</td>
            <td>
              <ul>
                <li v-for="perm in group.permissions" :key="perm.id">{{ perm.name }}</li>
              </ul>
            </td>
            <td>
              <button class="button is-info is-small mr-2" @click="showGroupModal(group)">Edit</button>
              <button class="button is-danger is-small" @click="deleteGroup(group.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Store Code Management -->
    <div v-if="activeTab === 'storecodes'">
      <button class="button is-small is-primary mb-3" @click="showStoreCodeModal(null)">Add New Department Code</button>

      <table class="table is-striped is-fullwidth is-size-7">
        <thead>
          <tr>
            <th>Department Code</th>
            <th>Department Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="storecode in storeCodes" :key="storecode.id">
            <td>{{ storecode.code }}</td>
            <td>{{ storecode.name }}</td>
            <td>
              <button class="button is-info is-small mr-2" @click="showStoreCodeModal(storecode)">Edit</button>
              <button class="button is-danger is-small" @click="deleteStoreCode(storecode.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Group Modal -->
    <div v-if="groupModalVisible" class="modal is-active">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ editingGroup ? 'Edit Group' : 'Add New Group' }}</p>
          <button class="delete" aria-label="close" @click="closeModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <label class="label">Group Name</label>
            <div class="control">
              <input class="input" v-model="editingGroup.name" />
            </div>
          </div>
          <div class="field">
            <label class="label">Permissions</label>
            <div class="control">
              <div v-for="perm in allPermissions" :key="perm.id">
                <label class="checkbox">
                  <input type="checkbox" :value="perm.id" v-model="editingGroup.selectedPermissions" />
                  {{ perm.name }}
                </label>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="confirmSave">Save</button>
          <button class="button" @click="closeModal">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Store Code Modal -->
    <div v-if="storeCodeModalVisible" class="modal is-active">
      <div class="modal-background" @click="closeStoreCodeModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ editingStoreCode ? 'Edit Store Code' : 'Add New Store Code' }}</p>
          <button class="delete" aria-label="close" @click="closeStoreCodeModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <label class="label">Store Code</label>
            <input class="input" v-model="editingStoreCode.code" />
          </div>
          <div class="field">
            <label class="label">Store Name</label>
            <input class="input" v-model="editingStoreCode.name" />
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="confirmSaveStoreCode">Save</button>
          <button class="button" @click="closeStoreCodeModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { toast } from 'bulma-toast';

export default {
  name: 'GroupPermissionManagement',
  data() {
    return {
      activeTab: 'groups',
      groups: [],
      allPermissions: [],
      storeCodes: [],
      groupModalVisible: false,
      storeCodeModalVisible: false,
      editingGroup: null,
      originalGroupData: null,
      editingStoreCode: null,
    };
  },
  methods: {
    async fetchGroups() {
      try {
        const response = await axios.get('/api/v1/groups/');
        this.groups = response.data;
      } catch (error) {
        console.error('Error fetching groups:', error);
        toast({ message: 'Error fetching groups', type: 'is-danger' });
      }
    },
    async fetchStoreCodes() {
      try {
        const response = await axios.get('/api/v1/storecodes/');
        this.storeCodes = response.data.results || response.data;  // ✅ Handle paginated & non-paginated responses
      } catch (error) {
        console.error('Error fetching store codes:', error);
        toast({ message: 'Error fetching store codes', type: 'is-danger' });
      }
    },
    async fetchAllPermissions() {
      try {
        const response = await axios.get('/api/v1/permissions/');
        this.allPermissions = response.data;
      } catch (error) {
        console.error('Error fetching permissions:', error);
        toast({ message: 'Error fetching permissions', type: 'is-danger' });
      }
    },
    showGroupModal(group) {
      this.originalGroupData = group ? JSON.parse(JSON.stringify(group)) : null;
      this.editingGroup = group ? { ...group, selectedPermissions: group.permissions.map(p => p.id) } : { name: '', selectedPermissions: [] };
      this.groupModalVisible = true;
    },
    showStoreCodeModal(storeCode) {
      this.editingStoreCode = storeCode ? { ...storeCode } : { code: '', name: '' };
      this.storeCodeModalVisible = true;
    },
    confirmSave() {
      if (JSON.stringify(this.editingGroup) !== JSON.stringify(this.originalGroupData)) {
        if (confirm('You have made changes. Do you want to save them?')) {
          this.saveGroup();
        }
      } else {
        this.modalVisible = false;
      }
    },
    confirmSaveStoreCode() {
      this.saveStoreCode();
    },
    async saveGroup() {
      let action = this.editingGroup.id ? "edit" : "create";
      let reason = prompt("Please enter a reason for this request:");

      if (!reason) {
        alert("A reason is required to submit this request.");
        return;
      }

      try {
        await axios.post("/api/v1/group-change-request/", {
          action: action,
          group_id: this.editingGroup.id,
          name: this.editingGroup.name,
          permissions: this.editingGroup.selectedPermissions,
          reason: reason
        });

        toast({ message: `Group ${action} request submitted for approval.`, type: "is-success" });
        this.groupModalVisible = false;
      } catch (error) {
        console.error(`Error submitting ${action} request:`, error);
        toast({ message: `Error submitting ${action} request.`, type: "is-danger" });
      }
    },
    async downloadAdminReportCsv() {
      try {
        const response = await axios.get('/api/v1/groups-admin-report-csv/', {
          responseType: 'blob'  // Important for file downloads
        });

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'admin_groups_permissions_report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({ message: 'Admin CSV report downloaded.', type: 'is-success' });
      } catch (error) {
        console.error('CSV download error:', error);
        toast({ message: 'Failed to download Admin CSV report.', type: 'is-danger' });
      }
    },
    async saveStoreCode() {
      const action = this.editingStoreCode.id ? "edit" : "create";
      const reason = prompt("Please enter a reason for this request:");

      await axios.post("/api/v1/storecode-change-request/", {
        action,
        storecode_id: this.editingStoreCode.id,
        code: this.editingStoreCode.code,
        name: this.editingStoreCode.name,
        reason
      });

      toast({ message: `Store Code ${action} request submitted.`, type: "is-success" });
      this.storeCodeModalVisible = false;
    },
    async deleteStoreCode(storeCodeId) {
      if (!confirm("Are you sure you want to request deletion of this store code?")) return;
      const reason = prompt("Please enter a reason for this request:");

      await axios.post("/api/v1/storecode-change-request/", {
        action: "delete",
        storecode_id: storeCodeId,
        reason
      });

      toast({ message: "Store Code deletion request submitted.", type: "is-success" });
    },
    closeModal() {
      if (JSON.stringify(this.editingGroup) !== JSON.stringify(this.originalGroupData)) {
        if (!confirm('You have unsaved changes. Do you want to leave?')) return;
      }
      this.groupModalVisible = false;
      window.removeEventListener('beforeunload', this.preventNavigation);
    },
    closeStoreCodeModal() {
      this.storeCodeModalVisible = false;
      window.removeEventListener('beforeunload', this.preventNavigation);
    },
    async deleteGroup(groupId) {
      if (!confirm("Are you sure you want to request deletion of this group?")) return;
      let reason = prompt("Please enter a reason for this request:");
      if (!reason) {
        alert("A reason is required to submit this request.");
        return;
      }

      try {
        await axios.post("/api/v1/group-change-request/", {
          action: "delete",
          group_id: groupId,
          reason : reason
        });

        toast({ message: "Group deletion request submitted for approval.", type: "is-success" });
      } catch (error) {
        console.error("Error requesting group deletion:", error);
        toast({ message: "Error requesting group deletion.", type: "is-danger" });
      }
    },
    preventNavigation(event) {
      event.preventDefault();
      event.returnValue = '';
    }
  },
  async created() {
    await this.fetchGroups();
    await this.fetchAllPermissions();
    await this.fetchStoreCodes();
  }
};
</script>

<style scoped>
.container {
  max-width: 900px;
  margin: 0 auto;
}
.table {
  width: 100%;
  margin-top: 20px;
}
.modal-card {
  width: 600px;
}
</style>